/**
 * Model catalogue behind the taxi valuation calculator (/taxi-valuation).
 *
 * This is deliberately a separate list from `newTaxiModels.js`. That file is a
 * research catalogue for people buying NEW — it only covers what is on sale
 * today. Valuation has to price what is actually in the used market, which is
 * mostly vehicles you cannot buy new any more (Etios, Tavera, Xylo, Indigo eCS,
 * old Innova). Those carry `discontinued: true` and a `newPrice` that is the
 * *current equivalent* ex-showroom price, because a used buyer always prices
 * against what a comparable vehicle costs today, not against a 2014 invoice.
 *
 * The list is wide on purpose. It has to cover everything BST actually lists —
 * which is not only app-cab hatchbacks but corporate sedans, wedding-fleet
 * luxury cars, EV fleet cars, tempo travellers and school buses — plus the
 * models a seller is likely to type even when we have none in stock. A seller
 * who cannot find their vehicle bounces, and that is a lead lost.
 *
 * PRICES GO STALE. `newPrice` is ex-showroom Delhi, commercial/Tour trim where
 * one exists, as of `PRICES_AS_OF`. Re-verify against the manufacturer's
 * commercial channel before trusting a number here — the whole calculator is
 * anchored on this column.
 *
 * The luxury block (Mercedes, BMW, Audi, Volvo, Lexus, Land Rover, Porsche,
 * Toyota's chauffeur cars) was checked against live listed prices on 2026-08-07
 * and corrected — the BMW X6 was out by 60%, the Mini Cooper by 34%, the
 * S-Class by 19%. Everything else in this file is still an estimate. Models no
 * longer listed for sale were flagged `discontinued` at the same time (Audi A8
 * and e-tron, BMW X4, Mercedes A-Class/GLB/EQE, Toyota Alphard and Prado,
 * Volvo XC40 petrol).
 *
 * Fields
 *   newPrice   – current ex-showroom (₹) of this model or its closest equivalent
 *   curve      – which depreciation curve applies: 'car' | 'muv' | 'van' |
 *                'bus' | 'luxury'.
 *                'muv' is the rebuild-worthy class (Innova, Fortuner, Traveller,
 *                Winger) that operators keep running long after a Dzire of the
 *                same age has been written off.
 *                'luxury' is the wedding-fleet and corporate chauffeur class,
 *                which falls off a cliff the day the warranty ends and then
 *                flattens out — see the curve's own note.
 *   demand     – resale demand multiplier, read *against the other models on the
 *                same curve*. A Dzire is worth more used than its ex-showroom
 *                implies because every fleet wants one; a Verito is worth less
 *                for the reverse reason; an E-Class outsells a Taycan in the
 *                wedding trade by the same logic. Kept in a narrow 0.82–1.08
 *                band — anything bigger than that belongs in the curve.
 *   scrapFloor – realistic end-of-life value (₹): body + engine to a scrapper or
 *                a parts buyer. Nothing is ever valued below this.
 *   fuels      – fuel types this model was actually sold/retrofitted with
 *   group      – which optgroup it sits in on the calculator's model picker.
 *                `category` still has to be one of the three the DB allows
 *                ('Cars' | 'Mini Vans' | 'Buses'), which is far too coarse to
 *                pick from once the list runs past a hundred entries.
 *   aliases    – lowercase strings a seller might actually type. Sellers write
 *                "Swift Dzire VDI" or "Crysta 2.4 GX", never our display name,
 *                so `findModel` matches on these. Longest match wins, which is
 *                why 'innova crysta' has to exist alongside 'innova'. A few
 *                aliases are outright misspellings ('dezire', 'wagnor',
 *                'rumiyon', 'tycan') — those are lifted from live listings, not
 *                invented.
 *   newTaxiSlug– link to the /new-taxi research page when we have one
 */

export const PRICES_AS_OF = 'July 2026';

const HATCH = 'Hatchbacks & compact sedans';
const SEDAN = 'Sedans & premium sedans';
const MUV = 'MUVs, SUVs & 7-seaters';
const EV = 'Electric fleet cars';
const LUX = 'Luxury & chauffeur-driven';
const VAN = 'Mini vans & tempo travellers';
const BUS = 'Buses & staff carriers';

export const valuationModels = [
  // ── Hatchbacks & compact sedans — the app-cab core ───────────────────────
  {
    id: 'maruti-dzire',
    brand: 'Maruti Suzuki',
    model: 'Swift Dzire / Dzire Tour S',
    aliases: ['swift dzire', 'swift dezire', 'dzire tour', 'dzire', 'dezire'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 716843,
    demand: 1.06,
    scrapFloor: 55000,
    fuels: ['CNG', 'Petrol', 'Diesel'],
    newTaxiSlug: 'maruti-dzire-tour-s',
  },
  {
    id: 'maruti-swift',
    brand: 'Maruti Suzuki',
    model: 'Swift',
    aliases: ['swift'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 579000,
    demand: 1.0,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol', 'Diesel'],
    newTaxiSlug: 'maruti-swift',
  },
  {
    id: 'maruti-wagonr',
    brand: 'Maruti Suzuki',
    model: 'WagonR / WagonR Tour H3',
    aliases: ['wagon r', 'wagonr', 'wagnor'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 588900,
    demand: 1.0,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
    newTaxiSlug: 'maruti-wagonr-tour-h3',
  },
  {
    id: 'maruti-celerio',
    brand: 'Maruti Suzuki',
    model: 'Celerio / Celerio Tour H2',
    aliases: ['celerio tour', 'celerio'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 565000,
    demand: 0.96,
    scrapFloor: 40000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'maruti-alto',
    brand: 'Maruti Suzuki',
    model: 'Alto / Alto K10',
    aliases: ['alto k10', 'alto 800', 'alto'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 424000,
    demand: 0.9,
    scrapFloor: 32000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'maruti-spresso',
    brand: 'Maruti Suzuki',
    model: 'S-Presso',
    aliases: ['s presso', 'spresso'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 433000,
    demand: 0.92,
    scrapFloor: 35000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'maruti-baleno',
    brand: 'Maruti Suzuki',
    model: 'Baleno',
    aliases: ['baleno'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 665000,
    demand: 0.93,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'hyundai-aura',
    brand: 'Hyundai',
    model: 'Aura',
    aliases: ['aura'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 599990,
    demand: 1.0,
    scrapFloor: 50000,
    fuels: ['CNG', 'Petrol', 'Diesel'],
    newTaxiSlug: 'hyundai-aura-cng',
  },
  {
    id: 'hyundai-xcent',
    brand: 'Hyundai',
    model: 'Xcent / Xcent Prime',
    aliases: ['xcent'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 650000,
    demand: 0.92,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'hyundai-grand-i10',
    brand: 'Hyundai',
    model: 'Grand i10 / i10 Nios',
    aliases: ['grand i10', 'i10 nios', 'nios', 'i10'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 620000,
    demand: 0.95,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'hyundai-exter',
    brand: 'Hyundai',
    model: 'Exter',
    aliases: ['exter'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 613000,
    demand: 0.95,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'hyundai-i20',
    brand: 'Hyundai',
    model: 'i20 / Elite i20',
    aliases: ['elite i20', 'i20'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 745000,
    demand: 0.9,
    scrapFloor: 50000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'hyundai-santro',
    brand: 'Hyundai',
    model: 'Santro / Santro Xing',
    aliases: ['santro xing', 'santro'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 570000,
    demand: 0.88,
    scrapFloor: 35000,
    fuels: ['CNG', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'hyundai-eon',
    brand: 'Hyundai',
    model: 'Eon',
    aliases: ['eon'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 465000,
    demand: 0.85,
    scrapFloor: 30000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'honda-amaze',
    brand: 'Honda',
    model: 'Amaze',
    aliases: ['amaze'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 751000,
    demand: 0.98,
    scrapFloor: 50000,
    fuels: ['Petrol', 'CNG', 'Diesel'],
    newTaxiSlug: 'honda-amaze',
  },
  {
    id: 'toyota-glanza',
    brand: 'Toyota',
    model: 'Glanza',
    aliases: ['glanza'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 673000,
    demand: 0.93,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'tata-tigor',
    brand: 'Tata',
    model: 'Tigor / Xpres-T (petrol & CNG)',
    aliases: ['xpres t', 'xprest', 'tigor'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 559000,
    demand: 0.94,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
    newTaxiSlug: 'tata-xpres-t-tigor',
  },
  {
    id: 'tata-tiago',
    brand: 'Tata',
    model: 'Tiago',
    aliases: ['tiago'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 565000,
    demand: 0.94,
    scrapFloor: 40000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'tata-altroz',
    brand: 'Tata',
    model: 'Altroz',
    aliases: ['altroz'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 665000,
    demand: 0.89,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol', 'Diesel'],
  },
  {
    id: 'tata-punch',
    brand: 'Tata',
    model: 'Punch',
    aliases: ['punch'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 620000,
    demand: 0.92,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'tata-zest',
    brand: 'Tata',
    model: 'Zest',
    aliases: ['zest'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 600000,
    demand: 0.88,
    scrapFloor: 45000,
    fuels: ['Diesel', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'tata-indigo',
    brand: 'Tata',
    model: 'Indica / Indigo eCS',
    aliases: ['indigo ecs', 'indigo', 'indica'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 500000,
    demand: 0.85,
    scrapFloor: 40000,
    fuels: ['Diesel', 'CNG'],
    discontinued: true,
  },
  {
    id: 'toyota-etios',
    brand: 'Toyota',
    model: 'Etios / Etios Liva',
    aliases: ['etios liva', 'etios'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 800000,
    demand: 0.98,
    scrapFloor: 55000,
    fuels: ['Diesel', 'Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'renault-kwid',
    brand: 'Renault',
    model: 'Kwid',
    aliases: ['kwid'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 470000,
    demand: 0.86,
    scrapFloor: 30000,
    fuels: ['Petrol'],
  },
  {
    id: 'nissan-magnite',
    brand: 'Nissan',
    model: 'Magnite',
    aliases: ['magnite'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 620000,
    demand: 0.9,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'ford-figo',
    brand: 'Ford',
    model: 'Figo',
    aliases: ['figo'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 700000,
    demand: 0.85,
    scrapFloor: 40000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'ford-aspire',
    brand: 'Ford',
    model: 'Aspire',
    aliases: ['figo aspire', 'aspire'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 800000,
    demand: 0.85,
    scrapFloor: 45000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'chevrolet-beat',
    brand: 'Chevrolet',
    model: 'Beat',
    aliases: ['beat'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 500000,
    demand: 0.82,
    scrapFloor: 30000,
    fuels: ['Diesel', 'Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'mahindra-verito',
    brand: 'Mahindra',
    model: 'Verito',
    aliases: ['verito'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 650000,
    demand: 0.88,
    scrapFloor: 40000,
    fuels: ['Diesel', 'Electric'],
    discontinued: true,
  },

  {
    id: 'maruti-800',
    brand: 'Maruti Suzuki',
    model: '800',
    aliases: ['maruti 800', 'm800', '800'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 350000,
    demand: 0.84,
    scrapFloor: 25000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'maruti-zen',
    brand: 'Maruti Suzuki',
    model: 'Zen / Zen Estilo',
    aliases: ['zen estilo', 'zen'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 400000,
    demand: 0.85,
    scrapFloor: 28000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'maruti-ritz',
    brand: 'Maruti Suzuki',
    model: 'Ritz',
    aliases: ['ritz'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 500000,
    demand: 0.87,
    scrapFloor: 32000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'maruti-astar',
    brand: 'Maruti Suzuki',
    model: 'A-Star',
    aliases: ['a star', 'astar'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 430000,
    demand: 0.85,
    scrapFloor: 28000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'maruti-esteem',
    brand: 'Maruti Suzuki',
    model: 'Esteem',
    aliases: ['esteem'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 450000,
    demand: 0.83,
    scrapFloor: 30000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'maruti-ignis',
    brand: 'Maruti Suzuki',
    model: 'Ignis',
    aliases: ['ignis'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 600000,
    demand: 0.89,
    scrapFloor: 40000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'maruti-fronx',
    brand: 'Maruti Suzuki',
    model: 'Fronx',
    aliases: ['fronx'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 750000,
    demand: 0.93,
    scrapFloor: 50000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'hyundai-getz',
    brand: 'Hyundai',
    model: 'Getz',
    aliases: ['getz'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 500000,
    demand: 0.84,
    scrapFloor: 30000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'tata-vista',
    brand: 'Tata',
    model: 'Indica Vista',
    aliases: ['indica vista', 'vista'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 520000,
    demand: 0.85,
    scrapFloor: 35000,
    fuels: ['Diesel', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'tata-bolt',
    brand: 'Tata',
    model: 'Bolt',
    aliases: ['bolt'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 550000,
    demand: 0.85,
    scrapFloor: 35000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'tata-nano',
    brand: 'Tata',
    model: 'Nano',
    aliases: ['nano'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 300000,
    demand: 0.82,
    scrapFloor: 22000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'chevrolet-spark',
    brand: 'Chevrolet',
    model: 'Spark',
    aliases: ['spark'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 420000,
    demand: 0.83,
    scrapFloor: 28000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'chevrolet-sail',
    brand: 'Chevrolet',
    model: 'Sail / Aveo U-VA',
    aliases: ['sail uva', 'sail', 'aveo u va', 'aveo uva'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 500000,
    demand: 0.82,
    scrapFloor: 30000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'fiat-punto',
    brand: 'Fiat',
    model: 'Punto / Grande Punto',
    aliases: ['grande punto', 'punto'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 600000,
    demand: 0.84,
    scrapFloor: 38000,
    fuels: ['Diesel', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'ford-ikon',
    brand: 'Ford',
    model: 'Ikon',
    aliases: ['ikon'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 550000,
    demand: 0.85,
    scrapFloor: 35000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'ford-fiesta',
    brand: 'Ford',
    model: 'Fiesta / Ford Classic',
    aliases: ['fiesta classic', 'fiesta', 'ford classic'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 700000,
    demand: 0.85,
    scrapFloor: 40000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'ford-freestyle',
    brand: 'Ford',
    model: 'Freestyle',
    aliases: ['freestyle'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 750000,
    demand: 0.86,
    scrapFloor: 45000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'nissan-micra',
    brand: 'Nissan',
    model: 'Micra',
    aliases: ['micra'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 600000,
    demand: 0.86,
    scrapFloor: 38000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'renault-pulse',
    brand: 'Renault',
    model: 'Pulse / Scala',
    aliases: ['pulse', 'scala'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 600000,
    demand: 0.84,
    scrapFloor: 36000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'volkswagen-polo',
    brand: 'Volkswagen',
    model: 'Polo',
    aliases: ['polo'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 750000,
    demand: 0.88,
    scrapFloor: 45000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'skoda-fabia',
    brand: 'Skoda',
    model: 'Fabia',
    aliases: ['fabia'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 650000,
    demand: 0.85,
    scrapFloor: 40000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'datsun-go',
    brand: 'Datsun',
    model: 'Go / Go+ / redi-GO',
    aliases: ['datsun go', 'go plus', 'redi go', 'redigo'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 450000,
    demand: 0.84,
    scrapFloor: 30000,
    fuels: ['Petrol'],
    discontinued: true,
  },
  {
    id: 'daewoo-matiz',
    brand: 'Daewoo',
    model: 'Matiz',
    aliases: ['matiz'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 350000,
    demand: 0.82,
    scrapFloor: 25000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'citroen-c3',
    brand: 'Citroen',
    model: 'C3',
    aliases: ['c3'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 640000,
    demand: 0.88,
    scrapFloor: 42000,
    fuels: ['Petrol'],
  },
  {
    id: 'citroen-c3-aircross',
    brand: 'Citroen',
    model: 'C3 Aircross',
    aliases: ['c3 aircross'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 980000,
    demand: 0.88,
    scrapFloor: 60000,
    fuels: ['Petrol'],
  },
  {
    id: 'premier-padmini',
    brand: 'Premier',
    model: 'Padmini',
    aliases: ['premier padmini', 'padmini'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 300000,
    demand: 0.82,
    scrapFloor: 25000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'hindustan-ambassador',
    brand: 'Hindustan Motors',
    model: 'Ambassador',
    aliases: ['ambassador', 'amby'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 500000,
    demand: 0.86,
    scrapFloor: 35000,
    fuels: ['Diesel', 'Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'honda-brio',
    brand: 'Honda',
    model: 'Brio',
    aliases: ['brio'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 550000,
    demand: 0.85,
    scrapFloor: 35000,
    fuels: ['Petrol'],
    discontinued: true,
  },
  {
    id: 'honda-jazz',
    brand: 'Honda',
    model: 'Jazz',
    aliases: ['jazz'],
    category: 'Cars',
    group: HATCH,
    curve: 'car',
    newPrice: 800000,
    demand: 0.86,
    scrapFloor: 48000,
    fuels: ['Petrol'],
    discontinued: true,
  },

  // ── Sedans — corporate, hotel and airport duty ───────────────────────────
  {
    id: 'maruti-ciaz',
    brand: 'Maruti Suzuki',
    model: 'Ciaz',
    aliases: ['ciaz'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 967000,
    demand: 0.95,
    scrapFloor: 55000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'honda-city',
    brand: 'Honda',
    model: 'City',
    aliases: ['honda city', 'city'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1200000,
    demand: 0.94,
    scrapFloor: 60000,
    fuels: ['Petrol', 'Diesel', 'Hybrid'],
  },
  {
    id: 'hyundai-verna',
    brand: 'Hyundai',
    model: 'Verna / Fluidic Verna',
    aliases: ['fluidic verna', 'verna'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1121000,
    demand: 0.92,
    scrapFloor: 60000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'hyundai-accent',
    brand: 'Hyundai',
    model: 'Accent',
    aliases: ['accent'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 600000,
    demand: 0.85,
    scrapFloor: 35000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'volkswagen-vento',
    brand: 'Volkswagen',
    model: 'Vento',
    aliases: ['vento'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1200000,
    demand: 0.88,
    scrapFloor: 60000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'volkswagen-ameo',
    brand: 'Volkswagen',
    model: 'Ameo',
    aliases: ['ameo'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 750000,
    demand: 0.85,
    scrapFloor: 45000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'volkswagen-virtus',
    brand: 'Volkswagen',
    model: 'Virtus',
    aliases: ['virtus'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1189000,
    demand: 0.9,
    scrapFloor: 65000,
    fuels: ['Petrol'],
  },
  {
    id: 'skoda-rapid',
    brand: 'Skoda',
    model: 'Rapid / Slavia',
    aliases: ['rapid', 'slavia'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1189000,
    demand: 0.89,
    scrapFloor: 65000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'nissan-sunny',
    brand: 'Nissan',
    model: 'Sunny',
    aliases: ['sunny'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 900000,
    demand: 0.85,
    scrapFloor: 45000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'toyota-corolla-altis',
    brand: 'Toyota',
    model: 'Corolla Altis',
    aliases: ['corolla altis', 'corolla', 'altis'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1900000,
    demand: 0.9,
    scrapFloor: 90000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },

  {
    id: 'maruti-sx4',
    brand: 'Maruti Suzuki',
    model: 'SX4',
    aliases: ['sx4'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 750000,
    demand: 0.87,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'maruti-kizashi',
    brand: 'Maruti Suzuki',
    model: 'Kizashi',
    aliases: ['kizashi'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1800000,
    demand: 0.82,
    scrapFloor: 80000,
    fuels: ['Petrol'],
    discontinued: true,
  },
  {
    id: 'hyundai-elantra',
    brand: 'Hyundai',
    model: 'Elantra',
    aliases: ['elantra'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 2100000,
    demand: 0.88,
    scrapFloor: 90000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'hyundai-sonata',
    brand: 'Hyundai',
    model: 'Sonata',
    aliases: ['sonata'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 2500000,
    demand: 0.85,
    scrapFloor: 95000,
    fuels: ['Petrol'],
    discontinued: true,
  },
  {
    id: 'honda-civic',
    brand: 'Honda',
    model: 'Civic',
    aliases: ['civic'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 2000000,
    demand: 0.88,
    scrapFloor: 90000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'toyota-yaris',
    brand: 'Toyota',
    model: 'Yaris',
    aliases: ['yaris'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1100000,
    demand: 0.88,
    scrapFloor: 60000,
    fuels: ['Petrol'],
    discontinued: true,
  },
  {
    id: 'tata-manza',
    brand: 'Tata',
    model: 'Indigo Manza',
    aliases: ['indigo manza', 'manza'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 650000,
    demand: 0.85,
    scrapFloor: 40000,
    fuels: ['Diesel', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'fiat-linea',
    brand: 'Fiat',
    model: 'Linea',
    aliases: ['linea'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 900000,
    demand: 0.84,
    scrapFloor: 50000,
    fuels: ['Diesel', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'chevrolet-optra',
    brand: 'Chevrolet',
    model: 'Optra',
    aliases: ['optra magnum', 'optra'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 900000,
    demand: 0.82,
    scrapFloor: 50000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'chevrolet-cruze',
    brand: 'Chevrolet',
    model: 'Cruze',
    aliases: ['cruze'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1800000,
    demand: 0.82,
    scrapFloor: 85000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'mitsubishi-lancer',
    brand: 'Mitsubishi',
    model: 'Lancer / Cedia',
    aliases: ['lancer', 'cedia'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 800000,
    demand: 0.83,
    scrapFloor: 45000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'renault-fluence',
    brand: 'Renault',
    model: 'Fluence',
    aliases: ['fluence'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 1200000,
    demand: 0.82,
    scrapFloor: 55000,
    fuels: ['Diesel', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'opel-corsa',
    brand: 'Opel',
    model: 'Corsa / Astra',
    aliases: ['corsa', 'astra'],
    category: 'Cars',
    group: SEDAN,
    curve: 'car',
    newPrice: 450000,
    demand: 0.82,
    scrapFloor: 28000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },

  // ── MUVs, SUVs & 7-seaters — outstation, corporate and airport work ──────
  {
    id: 'maruti-ertiga',
    brand: 'Maruti Suzuki',
    model: 'Ertiga / Ertiga Tour M',
    aliases: ['ertiga tour', 'ertiga'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 986440,
    demand: 1.05,
    scrapFloor: 70000,
    fuels: ['CNG', 'Petrol', 'Diesel'],
    newTaxiSlug: 'maruti-ertiga-tour-m',
  },
  {
    id: 'maruti-xl6',
    brand: 'Maruti Suzuki',
    model: 'XL6',
    aliases: ['xl6'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1160000,
    demand: 0.97,
    scrapFloor: 75000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'maruti-brezza',
    brand: 'Maruti Suzuki',
    model: 'Brezza / Vitara Brezza',
    aliases: ['vitara brezza', 'brezza'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 874000,
    demand: 0.96,
    scrapFloor: 60000,
    fuels: ['CNG', 'Petrol', 'Diesel'],
  },
  {
    id: 'maruti-grand-vitara',
    brand: 'Maruti Suzuki',
    model: 'Grand Vitara',
    aliases: ['grand vitara'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1099000,
    demand: 0.95,
    scrapFloor: 70000,
    fuels: ['CNG', 'Petrol', 'Hybrid'],
  },
  {
    id: 'maruti-invicto',
    brand: 'Maruti Suzuki',
    model: 'Invicto',
    aliases: ['invicto'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 2560000,
    demand: 1.0,
    scrapFloor: 130000,
    fuels: ['Hybrid', 'Petrol'],
  },
  {
    id: 'toyota-rumion',
    brand: 'Toyota',
    model: 'Rumion',
    aliases: ['rumion', 'rumiyon'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 979000,
    demand: 0.97,
    scrapFloor: 70000,
    fuels: ['CNG', 'Petrol'],
    newTaxiSlug: 'toyota-rumion',
  },
  {
    id: 'toyota-hyryder',
    brand: 'Toyota',
    model: 'Urban Cruiser Hyryder',
    aliases: ['urban cruiser hyryder', 'hyryder'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1129000,
    demand: 0.95,
    scrapFloor: 70000,
    fuels: ['CNG', 'Petrol', 'Hybrid'],
  },
  {
    id: 'toyota-innova-crysta',
    brand: 'Toyota',
    model: 'Innova Crysta',
    aliases: ['innova crysta', 'crysta'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1972000,
    demand: 1.08,
    scrapFloor: 120000,
    fuels: ['Diesel', 'Petrol'],
    newTaxiSlug: 'toyota-innova-crysta',
  },
  {
    id: 'toyota-innova-hycross',
    brand: 'Toyota',
    model: 'Innova Hycross',
    aliases: ['innova hycross', 'hycross'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1870000,
    demand: 1.02,
    scrapFloor: 120000,
    fuels: ['Petrol', 'Hybrid'],
    newTaxiSlug: 'toyota-innova-hycross',
  },
  {
    id: 'toyota-innova-old',
    brand: 'Toyota',
    model: 'Innova (Type 2 / older shape)',
    aliases: ['innova'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1600000,
    demand: 1.0,
    scrapFloor: 100000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'toyota-fortuner',
    brand: 'Toyota',
    model: 'Fortuner',
    aliases: ['fortuner'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 3476000,
    demand: 1.05,
    scrapFloor: 250000,
    fuels: ['Diesel', 'Petrol'],
  },
  {
    id: 'kia-carens',
    brand: 'Kia',
    model: 'Carens',
    aliases: ['carens'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1102000,
    demand: 0.96,
    scrapFloor: 80000,
    fuels: ['Petrol', 'Diesel'],
    newTaxiSlug: 'kia-carens',
  },
  {
    id: 'kia-seltos',
    brand: 'Kia',
    model: 'Seltos',
    aliases: ['seltos'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1129000,
    demand: 0.96,
    scrapFloor: 75000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'kia-sonet',
    brand: 'Kia',
    model: 'Sonet',
    aliases: ['sonet'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 800000,
    demand: 0.92,
    scrapFloor: 55000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'hyundai-creta',
    brand: 'Hyundai',
    model: 'Creta',
    aliases: ['creta'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1120000,
    demand: 0.98,
    scrapFloor: 75000,
    fuels: ['Petrol', 'Diesel', 'CNG'],
  },
  {
    id: 'hyundai-venue',
    brand: 'Hyundai',
    model: 'Venue',
    aliases: ['venue'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 795000,
    demand: 0.94,
    scrapFloor: 55000,
    fuels: ['Petrol', 'Diesel', 'CNG'],
  },
  {
    id: 'hyundai-alcazar',
    brand: 'Hyundai',
    model: 'Alcazar',
    aliases: ['alcazar'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1650000,
    demand: 0.95,
    scrapFloor: 95000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mahindra-marazzo',
    brand: 'Mahindra',
    model: 'Marazzo',
    aliases: ['marazzo'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1450000,
    demand: 0.95,
    scrapFloor: 90000,
    fuels: ['Diesel'],
  },
  {
    id: 'mahindra-bolero',
    brand: 'Mahindra',
    model: 'Bolero',
    aliases: ['bolero'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 995000,
    demand: 1.02,
    scrapFloor: 80000,
    fuels: ['Diesel'],
  },
  {
    id: 'mahindra-bolero-neo',
    brand: 'Mahindra',
    model: 'Bolero Neo / TUV300',
    aliases: ['bolero neo', 'tuv 300', 'tuv300'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1000000,
    demand: 0.95,
    scrapFloor: 70000,
    fuels: ['Diesel'],
  },
  {
    id: 'mahindra-scorpio',
    brand: 'Mahindra',
    model: 'Scorpio / Scorpio Classic / Scorpio N',
    aliases: ['scorpio classic', 'scorpio n', 'scorpio'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1399000,
    demand: 1.0,
    scrapFloor: 100000,
    fuels: ['Diesel', 'Petrol'],
  },
  {
    id: 'mahindra-xuv700',
    brand: 'Mahindra',
    model: 'XUV700',
    aliases: ['xuv 700', 'xuv700'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1499000,
    demand: 0.98,
    scrapFloor: 100000,
    fuels: ['Diesel', 'Petrol'],
  },
  {
    id: 'mahindra-xuv500',
    brand: 'Mahindra',
    model: 'XUV500',
    aliases: ['xuv 500', 'xuv500'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1400000,
    demand: 0.9,
    scrapFloor: 80000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'mahindra-xylo',
    brand: 'Mahindra',
    model: 'Xylo',
    aliases: ['xylo'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1100000,
    demand: 0.9,
    scrapFloor: 70000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'mahindra-kuv100',
    brand: 'Mahindra',
    model: 'KUV100',
    aliases: ['kuv 100', 'kuv100'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 600000,
    demand: 0.85,
    scrapFloor: 40000,
    fuels: ['Petrol', 'Diesel', 'CNG'],
    discontinued: true,
  },
  {
    id: 'tata-safari',
    brand: 'Tata',
    model: 'Safari / Safari Storme',
    aliases: ['safari storme', 'safari'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1650000,
    demand: 0.92,
    scrapFloor: 90000,
    fuels: ['Diesel'],
  },
  {
    id: 'tata-sumo',
    brand: 'Tata',
    model: 'Sumo / Sumo Gold',
    aliases: ['sumo gold', 'sumo victa', 'sumo'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 900000,
    demand: 0.88,
    scrapFloor: 60000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'tata-nexon',
    brand: 'Tata',
    model: 'Nexon',
    aliases: ['nexon'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 800000,
    demand: 0.94,
    scrapFloor: 55000,
    fuels: ['Petrol', 'Diesel', 'CNG'],
  },
  {
    id: 'chevrolet-tavera',
    brand: 'Chevrolet',
    model: 'Tavera',
    aliases: ['tavera'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1000000,
    demand: 0.9,
    scrapFloor: 65000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'chevrolet-enjoy',
    brand: 'Chevrolet',
    model: 'Enjoy',
    aliases: ['enjoy'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 800000,
    demand: 0.82,
    scrapFloor: 50000,
    fuels: ['Diesel', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'renault-lodgy',
    brand: 'Renault',
    model: 'Lodgy',
    aliases: ['lodgy'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1100000,
    demand: 0.9,
    scrapFloor: 70000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'renault-triber',
    brand: 'Renault',
    model: 'Triber',
    aliases: ['triber'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 620000,
    demand: 0.94,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'renault-duster',
    brand: 'Renault',
    model: 'Duster',
    aliases: ['duster'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1100000,
    demand: 0.88,
    scrapFloor: 65000,
    fuels: ['Diesel', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'ford-ecosport',
    brand: 'Ford',
    model: 'EcoSport',
    aliases: ['ecosport'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 900000,
    demand: 0.86,
    scrapFloor: 55000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'ford-endeavour',
    brand: 'Ford',
    model: 'Endeavour',
    aliases: ['endeavour', 'endeavor'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 3500000,
    demand: 0.88,
    scrapFloor: 200000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'mg-hector',
    brand: 'MG',
    model: 'Hector / Hector Plus',
    aliases: ['hector plus', 'hector'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1400000,
    demand: 0.9,
    scrapFloor: 80000,
    fuels: ['Petrol', 'Diesel'],
  },

  {
    id: 'toyota-qualis',
    brand: 'Toyota',
    model: 'Qualis',
    aliases: ['qualis'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1400000,
    demand: 0.94,
    scrapFloor: 85000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'toyota-urban-cruiser',
    brand: 'Toyota',
    model: 'Urban Cruiser / Taisor',
    aliases: ['urban cruiser', 'taisor'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 743000,
    demand: 0.9,
    scrapFloor: 55000,
    fuels: ['CNG', 'Petrol'],
  },
  {
    id: 'toyota-hilux',
    brand: 'Toyota',
    model: 'Hilux',
    aliases: ['hilux'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 3199000,
    demand: 0.9,
    scrapFloor: 180000,
    fuels: ['Diesel'],
  },
  {
    id: 'honda-mobilio',
    brand: 'Honda',
    model: 'Mobilio',
    aliases: ['mobilio'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 900000,
    demand: 0.88,
    scrapFloor: 55000,
    fuels: ['Diesel', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'honda-brv',
    brand: 'Honda',
    model: 'BR-V',
    aliases: ['br v', 'brv'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1100000,
    demand: 0.86,
    scrapFloor: 65000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'honda-wrv',
    brand: 'Honda',
    model: 'WR-V',
    aliases: ['wr v', 'wrv'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 900000,
    demand: 0.86,
    scrapFloor: 55000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'honda-elevate',
    brand: 'Honda',
    model: 'Elevate',
    aliases: ['elevate'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1130000,
    demand: 0.92,
    scrapFloor: 70000,
    fuels: ['Petrol'],
  },
  {
    id: 'honda-crv',
    brand: 'Honda',
    model: 'CR-V',
    aliases: ['cr v', 'crv'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 2500000,
    demand: 0.86,
    scrapFloor: 110000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'nissan-terrano',
    brand: 'Nissan',
    model: 'Terrano',
    aliases: ['terrano'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1100000,
    demand: 0.85,
    scrapFloor: 65000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'nissan-kicks',
    brand: 'Nissan',
    model: 'Kicks',
    aliases: ['kicks'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1000000,
    demand: 0.85,
    scrapFloor: 60000,
    fuels: ['Petrol'],
    discontinued: true,
  },
  {
    id: 'mahindra-thar',
    brand: 'Mahindra',
    model: 'Thar / Thar Roxx',
    aliases: ['thar roxx', 'thar'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1150000,
    demand: 0.9,
    scrapFloor: 80000,
    fuels: ['Diesel', 'Petrol'],
  },
  {
    id: 'mahindra-xuv300',
    brand: 'Mahindra',
    model: 'XUV300 / XUV 3XO',
    aliases: ['xuv 3xo', 'xuv3xo', 'xuv 300', 'xuv300'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 799000,
    demand: 0.92,
    scrapFloor: 55000,
    fuels: ['CNG', 'Petrol', 'Diesel'],
  },
  {
    id: 'mahindra-quanto',
    brand: 'Mahindra',
    model: 'Quanto / NuvoSport',
    aliases: ['quanto', 'nuvosport'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 800000,
    demand: 0.84,
    scrapFloor: 50000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'mahindra-alturas',
    brand: 'Mahindra',
    model: 'Alturas G4',
    aliases: ['alturas'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 3000000,
    demand: 0.85,
    scrapFloor: 150000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'mahindra-armada',
    brand: 'Mahindra',
    model: 'Armada / Commander / Major',
    aliases: ['armada', 'commander', 'major'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 800000,
    demand: 0.85,
    scrapFloor: 55000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'tata-aria',
    brand: 'Tata',
    model: 'Aria',
    aliases: ['aria'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1500000,
    demand: 0.83,
    scrapFloor: 85000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'tata-hexa',
    brand: 'Tata',
    model: 'Hexa',
    aliases: ['hexa'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1700000,
    demand: 0.88,
    scrapFloor: 95000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'tata-harrier',
    brand: 'Tata',
    model: 'Harrier',
    aliases: ['harrier'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1550000,
    demand: 0.92,
    scrapFloor: 90000,
    fuels: ['Diesel'],
  },
  {
    id: 'tata-curvv',
    brand: 'Tata',
    model: 'Curvv',
    aliases: ['curvv'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1000000,
    demand: 0.9,
    scrapFloor: 65000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'maruti-scross',
    brand: 'Maruti Suzuki',
    model: 'S-Cross',
    aliases: ['s cross', 'scross'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 900000,
    demand: 0.87,
    scrapFloor: 55000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'maruti-jimny',
    brand: 'Maruti Suzuki',
    model: 'Jimny',
    aliases: ['jimny'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1274000,
    demand: 0.86,
    scrapFloor: 80000,
    fuels: ['Petrol'],
  },
  {
    id: 'maruti-gypsy',
    brand: 'Maruti Suzuki',
    model: 'Gypsy',
    aliases: ['gypsy'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 700000,
    demand: 0.9,
    scrapFloor: 45000,
    fuels: ['Petrol'],
    discontinued: true,
  },
  {
    id: 'hyundai-tucson',
    brand: 'Hyundai',
    model: 'Tucson',
    aliases: ['tucson'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 2900000,
    demand: 0.86,
    scrapFloor: 130000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'hyundai-santafe',
    brand: 'Hyundai',
    model: 'Santa Fe',
    aliases: ['santa fe', 'santafe'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 3000000,
    demand: 0.83,
    scrapFloor: 130000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'kia-syros',
    brand: 'Kia',
    model: 'Syros',
    aliases: ['syros'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 900000,
    demand: 0.9,
    scrapFloor: 60000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mg-astor',
    brand: 'MG',
    model: 'Astor',
    aliases: ['astor'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1100000,
    demand: 0.88,
    scrapFloor: 65000,
    fuels: ['Petrol'],
  },
  {
    id: 'mg-gloster',
    brand: 'MG',
    model: 'Gloster',
    aliases: ['gloster'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 4000000,
    demand: 0.85,
    scrapFloor: 200000,
    fuels: ['Diesel'],
  },
  {
    id: 'jeep-compass',
    brand: 'Jeep',
    model: 'Compass',
    aliases: ['compass'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 2100000,
    demand: 0.88,
    scrapFloor: 110000,
    fuels: ['Diesel', 'Petrol'],
  },
  {
    id: 'jeep-meridian',
    brand: 'Jeep',
    model: 'Meridian',
    aliases: ['meridian'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 3400000,
    demand: 0.88,
    scrapFloor: 160000,
    fuels: ['Diesel', 'Petrol'],
  },
  {
    id: 'isuzu-mux',
    brand: 'Isuzu',
    model: 'MU-X',
    aliases: ['mu x', 'mux'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 3600000,
    demand: 0.86,
    scrapFloor: 180000,
    fuels: ['Diesel'],
  },
  {
    id: 'isuzu-dmax',
    brand: 'Isuzu',
    model: 'D-Max',
    aliases: ['d max', 'dmax'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 2800000,
    demand: 0.86,
    scrapFloor: 150000,
    fuels: ['Diesel'],
  },
  {
    id: 'renault-captur',
    brand: 'Renault',
    model: 'Captur',
    aliases: ['captur'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1200000,
    demand: 0.82,
    scrapFloor: 65000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'renault-kiger',
    brand: 'Renault',
    model: 'Kiger',
    aliases: ['kiger'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 620000,
    demand: 0.88,
    scrapFloor: 45000,
    fuels: ['Petrol'],
  },
  {
    id: 'volkswagen-tiguan',
    brand: 'Volkswagen',
    model: 'Tiguan',
    aliases: ['tiguan'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 3600000,
    demand: 0.88,
    scrapFloor: 180000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'volkswagen-taigun',
    brand: 'Volkswagen',
    model: 'Taigun',
    aliases: ['taigun'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1200000,
    demand: 0.9,
    scrapFloor: 70000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'skoda-kushaq',
    brand: 'Skoda',
    model: 'Kushaq',
    aliases: ['kushaq'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 1090000,
    demand: 0.9,
    scrapFloor: 65000,
    fuels: ['Petrol'],
  },
  {
    id: 'skoda-kylaq',
    brand: 'Skoda',
    model: 'Kylaq',
    aliases: ['kylaq'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 830000,
    demand: 0.9,
    scrapFloor: 55000,
    fuels: ['Petrol'],
  },
  {
    id: 'citroen-c5-aircross',
    brand: 'Citroen',
    model: 'C5 Aircross',
    aliases: ['c5 aircross'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 3700000,
    demand: 0.85,
    scrapFloor: 170000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'citroen-basalt',
    brand: 'Citroen',
    model: 'Basalt',
    aliases: ['basalt'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 850000,
    demand: 0.88,
    scrapFloor: 55000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'force-gurkha',
    brand: 'Force',
    model: 'Gurkha',
    aliases: ['gurkha'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 1800000,
    demand: 0.85,
    scrapFloor: 110000,
    fuels: ['Diesel'],
  },
  {
    id: 'mitsubishi-pajero',
    brand: 'Mitsubishi',
    model: 'Pajero / Montero',
    aliases: ['pajero sport', 'pajero', 'montero'],
    category: 'Cars',
    group: MUV,
    curve: 'muv',
    newPrice: 3500000,
    demand: 0.85,
    scrapFloor: 180000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'chevrolet-captiva',
    brand: 'Chevrolet',
    model: 'Captiva / Trailblazer',
    aliases: ['captiva', 'trailblazer'],
    category: 'Cars',
    group: MUV,
    curve: 'car',
    newPrice: 2500000,
    demand: 0.82,
    scrapFloor: 120000,
    fuels: ['Diesel'],
    discontinued: true,
  },

  // ── Electric fleet cars ──────────────────────────────────────────────────
  // Priced apart from their petrol twins on purpose: an Xpres-T EV costs more
  // than double a CNG Xpres-T new, and the battery makes the used curve behave
  // differently again. FUEL_FACTORS already discounts Electric for the smaller
  // operator pool.
  {
    id: 'tata-xpres-t-ev',
    brand: 'Tata',
    model: 'Xpres-T EV / Tigor EV',
    aliases: ['xpres t ev', 'xprest ev', 'tigor ev'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1249000,
    demand: 0.95,
    scrapFloor: 70000,
    fuels: ['Electric'],
    newTaxiSlug: 'tata-xpres-t-tigor',
  },
  {
    id: 'tata-tiago-ev',
    brand: 'Tata',
    model: 'Tiago EV',
    aliases: ['tiago ev'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 799000,
    demand: 0.9,
    scrapFloor: 50000,
    fuels: ['Electric'],
  },
  {
    id: 'tata-nexon-ev',
    brand: 'Tata',
    model: 'Nexon EV',
    aliases: ['nexon ev'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1249000,
    demand: 0.95,
    scrapFloor: 70000,
    fuels: ['Electric'],
  },
  {
    id: 'mg-windsor-ev',
    brand: 'MG',
    model: 'Windsor EV',
    aliases: ['windsor'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1400000,
    demand: 0.95,
    scrapFloor: 80000,
    fuels: ['Electric'],
  },
  {
    id: 'mg-zs-ev',
    brand: 'MG',
    model: 'ZS EV',
    aliases: ['zs ev', 'zsev'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1898000,
    demand: 0.9,
    scrapFloor: 95000,
    fuels: ['Electric'],
  },
  {
    id: 'citroen-ec3',
    brand: 'Citroen',
    model: 'eC3',
    aliases: ['ec3', 'e c3'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1257000,
    demand: 0.9,
    scrapFloor: 70000,
    fuels: ['Electric'],
  },
  {
    id: 'byd-e6',
    brand: 'BYD',
    model: 'e6',
    aliases: ['e6'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 2955000,
    demand: 0.9,
    scrapFloor: 150000,
    fuels: ['Electric'],
  },

  {
    id: 'tata-punch-ev',
    brand: 'Tata',
    model: 'Punch EV',
    aliases: ['punch ev'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1000000,
    demand: 0.92,
    scrapFloor: 60000,
    fuels: ['Electric'],
  },
  {
    id: 'tata-curvv-ev',
    brand: 'Tata',
    model: 'Curvv EV',
    aliases: ['curvv ev'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1750000,
    demand: 0.9,
    scrapFloor: 90000,
    fuels: ['Electric'],
  },
  {
    id: 'mahindra-e2o',
    brand: 'Mahindra',
    model: 'e2o / e-Verito',
    aliases: ['e2o', 'e verito'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 900000,
    demand: 0.82,
    scrapFloor: 50000,
    fuels: ['Electric'],
    discontinued: true,
  },
  {
    id: 'mahindra-xuv400-ev',
    brand: 'Mahindra',
    model: 'XUV400 EV',
    aliases: ['xuv 400', 'xuv400'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1550000,
    demand: 0.88,
    scrapFloor: 85000,
    fuels: ['Electric'],
  },
  {
    id: 'mahindra-be6',
    brand: 'Mahindra',
    model: 'BE 6',
    aliases: ['be 6', 'be6'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1890000,
    demand: 0.9,
    scrapFloor: 100000,
    fuels: ['Electric'],
  },
  {
    id: 'mahindra-xev9e',
    brand: 'Mahindra',
    model: 'XEV 9e',
    aliases: ['xev 9e', 'xev9e'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 2190000,
    demand: 0.9,
    scrapFloor: 110000,
    fuels: ['Electric'],
  },
  {
    id: 'hyundai-kona',
    brand: 'Hyundai',
    model: 'Kona Electric',
    aliases: ['kona'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 2400000,
    demand: 0.85,
    scrapFloor: 110000,
    fuels: ['Electric'],
    discontinued: true,
  },
  {
    id: 'hyundai-creta-ev',
    brand: 'Hyundai',
    model: 'Creta Electric',
    aliases: ['creta electric', 'creta ev'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1800000,
    demand: 0.92,
    scrapFloor: 90000,
    fuels: ['Electric'],
  },
  {
    id: 'hyundai-ioniq5',
    brand: 'Hyundai',
    model: 'Ioniq 5',
    aliases: ['ioniq'],
    category: 'Cars',
    group: EV,
    curve: 'luxury',
    newPrice: 4600000,
    demand: 0.85,
    scrapFloor: 180000,
    fuels: ['Electric'],
  },
  {
    id: 'kia-ev6',
    brand: 'Kia',
    model: 'EV6',
    aliases: ['ev6'],
    category: 'Cars',
    group: EV,
    curve: 'luxury',
    newPrice: 6600000,
    demand: 0.85,
    scrapFloor: 250000,
    fuels: ['Electric'],
  },
  {
    id: 'kia-ev9',
    brand: 'Kia',
    model: 'EV9',
    aliases: ['ev9'],
    category: 'Cars',
    group: EV,
    curve: 'luxury',
    newPrice: 13000000,
    demand: 0.85,
    scrapFloor: 450000,
    fuels: ['Electric'],
  },
  {
    id: 'mg-comet',
    brand: 'MG',
    model: 'Comet EV',
    aliases: ['comet'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 700000,
    demand: 0.85,
    scrapFloor: 40000,
    fuels: ['Electric'],
  },
  {
    id: 'maruti-evitara',
    brand: 'Maruti Suzuki',
    model: 'e Vitara',
    aliases: ['e vitara', 'evitara'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 1700000,
    demand: 0.9,
    scrapFloor: 90000,
    fuels: ['Electric'],
  },
  {
    id: 'byd-atto3',
    brand: 'BYD',
    model: 'Atto 3',
    aliases: ['atto 3', 'atto3'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 2500000,
    demand: 0.87,
    scrapFloor: 120000,
    fuels: ['Electric'],
  },
  {
    id: 'byd-seal',
    brand: 'BYD',
    model: 'Seal',
    aliases: ['seal'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 4100000,
    demand: 0.85,
    scrapFloor: 180000,
    fuels: ['Electric'],
  },
  {
    id: 'byd-emax7',
    brand: 'BYD',
    model: 'eMax 7',
    aliases: ['emax 7', 'emax7'],
    category: 'Cars',
    group: EV,
    curve: 'car',
    newPrice: 2700000,
    demand: 0.88,
    scrapFloor: 130000,
    fuels: ['Electric'],
  },
  {
    id: 'tesla-model-y',
    brand: 'Tesla',
    model: 'Model Y',
    aliases: ['model y'],
    category: 'Cars',
    group: EV,
    curve: 'luxury',
    newPrice: 6000000,
    demand: 0.86,
    scrapFloor: 250000,
    fuels: ['Electric'],
  },
  {
    id: 'tesla-model-3',
    brand: 'Tesla',
    model: 'Model 3',
    aliases: ['model 3'],
    category: 'Cars',
    group: EV,
    curve: 'luxury',
    newPrice: 6000000,
    demand: 0.86,
    scrapFloor: 250000,
    fuels: ['Electric'],
  },

  // ── Luxury & chauffeur-driven ────────────────────────────────────────────
  // Wedding fleets, hotel contracts and corporate chauffeur work. These use the
  // 'luxury' curve, not 'car': out of warranty a German sedan is priced by what
  // one gearbox job costs, and an ex-commercial one has a buyer pool of almost
  // nobody. Ex-showroom here is the common chauffeur-spec variant, not the top
  // trim, so verify against the actual variant before quoting.
  {
    id: 'toyota-camry',
    brand: 'Toyota',
    model: 'Camry',
    aliases: ['camry'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4869000,
    demand: 1.04,
    scrapFloor: 200000,
    fuels: ['Hybrid', 'Petrol'],
  },
  {
    id: 'toyota-vellfire',
    brand: 'Toyota',
    model: 'Vellfire',
    aliases: ['vellfire'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 12000000,
    demand: 0.98,
    scrapFloor: 500000,
    fuels: ['Hybrid'],
  },
  {
    id: 'toyota-alphard',
    brand: 'Toyota',
    model: 'Alphard',
    aliases: ['alphard'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 13500000,
    demand: 0.96,
    scrapFloor: 550000,
    fuels: ['Hybrid'],
    discontinued: true,
  },
  {
    id: 'lexus-es',
    brand: 'Lexus',
    model: 'ES 300h',
    aliases: ['es 300', 'es300'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6610000,
    demand: 0.95,
    scrapFloor: 280000,
    fuels: ['Hybrid'],
  },
  {
    id: 'mercedes-c-class',
    brand: 'Mercedes-Benz',
    model: 'C-Class',
    aliases: ['c class', 'c220', 'c200'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5990000,
    demand: 0.98,
    scrapFloor: 250000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mercedes-e-class',
    brand: 'Mercedes-Benz',
    model: 'E-Class',
    aliases: ['e class', 'e220', 'e200'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 8000000,
    demand: 1.05,
    scrapFloor: 300000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mercedes-s-class',
    brand: 'Mercedes-Benz',
    model: 'S-Class',
    aliases: ['s class', 's350', 's450'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 22000000,
    demand: 0.96,
    scrapFloor: 500000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mercedes-maybach',
    brand: 'Mercedes-Benz',
    model: 'Maybach S-Class',
    aliases: ['maybach s class', 'maybach'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 28800000,
    demand: 0.94,
    scrapFloor: 800000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mercedes-v-class',
    brand: 'Mercedes-Benz',
    model: 'V-Class',
    aliases: ['v class', 'vclass'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 14000000,
    demand: 1.04,
    scrapFloor: 450000,
    fuels: ['Diesel'],
  },
  {
    id: 'mercedes-glc',
    brand: 'Mercedes-Benz',
    model: 'GLC',
    aliases: ['glc'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 7700000,
    demand: 0.92,
    scrapFloor: 300000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mercedes-gle',
    brand: 'Mercedes-Benz',
    model: 'GLE',
    aliases: ['gle', 'ml 350', 'ml class'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 10000000,
    demand: 0.92,
    scrapFloor: 380000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'bmw-3-series',
    brand: 'BMW',
    model: '3 Series',
    aliases: ['3 series', '320d', '330i'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6060000,
    demand: 0.95,
    scrapFloor: 220000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'bmw-5-series',
    brand: 'BMW',
    model: '5 Series',
    aliases: ['5 series', '520d', '530i'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 7660000,
    demand: 1.02,
    scrapFloor: 300000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'bmw-7-series',
    brand: 'BMW',
    model: '7 Series',
    aliases: ['7 series', '730', '740'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 18300000,
    demand: 0.98,
    scrapFloor: 500000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'bmw-x1',
    brand: 'BMW',
    model: 'X1',
    aliases: ['x1'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5090000,
    demand: 0.96,
    scrapFloor: 220000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'bmw-x3',
    brand: 'BMW',
    model: 'X3',
    aliases: ['x3'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6990000,
    demand: 0.92,
    scrapFloor: 300000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'bmw-x5',
    brand: 'BMW',
    model: 'X5',
    aliases: ['x5'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 9690000,
    demand: 0.92,
    scrapFloor: 380000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'bmw-x7',
    brand: 'BMW',
    model: 'X7',
    aliases: ['x7'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 12600000,
    demand: 1.02,
    scrapFloor: 400000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'audi-a4',
    brand: 'Audi',
    model: 'A4',
    aliases: ['a4'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4688000,
    demand: 0.95,
    scrapFloor: 220000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'audi-a6',
    brand: 'Audi',
    model: 'A6',
    aliases: ['a6'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6374000,
    demand: 0.96,
    scrapFloor: 280000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'audi-a8',
    brand: 'Audi',
    model: 'A8 L',
    aliases: ['a8'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 13500000,
    demand: 0.92,
    scrapFloor: 450000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'audi-q3',
    brand: 'Audi',
    model: 'Q3',
    aliases: ['q3'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4367000,
    demand: 0.9,
    scrapFloor: 190000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'audi-q5',
    brand: 'Audi',
    model: 'Q5',
    aliases: ['q5'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6375000,
    demand: 0.92,
    scrapFloor: 280000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'audi-q7',
    brand: 'Audi',
    model: 'Q7',
    aliases: ['q7'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 8717000,
    demand: 0.92,
    scrapFloor: 330000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'audi-q8',
    brand: 'Audi',
    model: 'Q8',
    aliases: ['q8'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 11300000,
    demand: 0.9,
    scrapFloor: 420000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'volvo-xc60',
    brand: 'Volvo',
    model: 'XC60',
    aliases: ['xc60', 'xc 60'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6890000,
    demand: 0.9,
    scrapFloor: 290000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'volvo-xc90',
    brand: 'Volvo',
    model: 'XC90',
    aliases: ['xc90', 'xc 90'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 9780000,
    demand: 0.9,
    scrapFloor: 380000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'volvo-s90',
    brand: 'Volvo',
    model: 'S90',
    aliases: ['s90'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6800000,
    demand: 0.88,
    scrapFloor: 260000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'volvo-v90',
    brand: 'Volvo',
    model: 'V90 Cross Country',
    aliases: ['v90', 'v 90'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 7000000,
    demand: 0.86,
    scrapFloor: 270000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'landrover-range-rover',
    brand: 'Land Rover',
    model: 'Range Rover',
    aliases: ['range rover'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 24000000,
    demand: 0.92,
    scrapFloor: 700000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'landrover-range-rover-sport',
    brand: 'Land Rover',
    model: 'Range Rover Sport',
    aliases: ['range rover sport'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 14300000,
    demand: 0.92,
    scrapFloor: 500000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'landrover-discovery',
    brand: 'Land Rover',
    model: 'Discovery',
    aliases: ['discovery'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 12500000,
    demand: 0.88,
    scrapFloor: 400000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'jaguar-xf',
    brand: 'Jaguar',
    model: 'XF',
    aliases: ['xf'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 7500000,
    demand: 0.88,
    scrapFloor: 290000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'jaguar-xj',
    brand: 'Jaguar',
    model: 'XJ',
    aliases: ['xj'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 10000000,
    demand: 0.86,
    scrapFloor: 350000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'porsche-cayenne',
    brand: 'Porsche',
    model: 'Cayenne',
    aliases: ['cayenne'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 13900000,
    demand: 0.9,
    scrapFloor: 550000,
    fuels: ['Petrol'],
  },
  {
    id: 'porsche-panamera',
    brand: 'Porsche',
    model: 'Panamera',
    aliases: ['panamera'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 17100000,
    demand: 0.88,
    scrapFloor: 600000,
    fuels: ['Petrol'],
  },
  {
    id: 'porsche-taycan',
    brand: 'Porsche',
    model: 'Taycan',
    aliases: ['taycan', 'tycan'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 17000000,
    demand: 0.85,
    scrapFloor: 600000,
    fuels: ['Electric'],
  },
  {
    id: 'kia-carnival',
    brand: 'Kia',
    model: 'Carnival',
    aliases: ['carnival'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5965000,
    demand: 1.05,
    scrapFloor: 260000,
    fuels: ['Diesel'],
  },

  {
    id: 'honda-accord',
    brand: 'Honda',
    model: 'Accord',
    aliases: ['accord'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4500000,
    demand: 0.9,
    scrapFloor: 150000,
    fuels: ['Hybrid', 'Petrol'],
    discontinued: true,
  },
  {
    id: 'skoda-octavia',
    brand: 'Skoda',
    model: 'Octavia / Laura',
    aliases: ['octavia', 'laura'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 2800000,
    demand: 0.95,
    scrapFloor: 110000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'skoda-superb',
    brand: 'Skoda',
    model: 'Superb',
    aliases: ['superb'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5800000,
    demand: 0.98,
    scrapFloor: 230000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'skoda-kodiaq',
    brand: 'Skoda',
    model: 'Kodiaq',
    aliases: ['kodiaq'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4600000,
    demand: 0.9,
    scrapFloor: 180000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'volkswagen-jetta',
    brand: 'Volkswagen',
    model: 'Jetta',
    aliases: ['jetta'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 2500000,
    demand: 0.9,
    scrapFloor: 100000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'volkswagen-passat',
    brand: 'Volkswagen',
    model: 'Passat',
    aliases: ['passat'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 3500000,
    demand: 0.9,
    scrapFloor: 140000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'toyota-land-cruiser',
    brand: 'Toyota',
    model: 'Land Cruiser LC300',
    aliases: ['land cruiser'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 21800000,
    demand: 0.95,
    scrapFloor: 750000,
    fuels: ['Diesel'],
  },
  {
    id: 'toyota-prado',
    brand: 'Toyota',
    model: 'Land Cruiser Prado',
    aliases: ['land cruiser prado', 'prado'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 10000000,
    demand: 0.95,
    scrapFloor: 380000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'mercedes-a-class',
    brand: 'Mercedes-Benz',
    model: 'A-Class Limousine',
    aliases: ['a class', 'a200', 'a180'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4600000,
    demand: 0.9,
    scrapFloor: 190000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'mercedes-cla',
    brand: 'Mercedes-Benz',
    model: 'CLA',
    aliases: ['cla'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5650000,
    demand: 0.9,
    scrapFloor: 230000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mercedes-gla',
    brand: 'Mercedes-Benz',
    model: 'GLA',
    aliases: ['gla'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5180000,
    demand: 0.9,
    scrapFloor: 210000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mercedes-glb',
    brand: 'Mercedes-Benz',
    model: 'GLB',
    aliases: ['glb'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6800000,
    demand: 0.9,
    scrapFloor: 270000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'mercedes-gls',
    brand: 'Mercedes-Benz',
    model: 'GLS',
    aliases: ['gls', 'gl 350'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 13200000,
    demand: 0.92,
    scrapFloor: 450000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'mercedes-eqs',
    brand: 'Mercedes-Benz',
    model: 'EQS',
    aliases: ['eqs'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 13000000,
    demand: 0.85,
    scrapFloor: 500000,
    fuels: ['Electric'],
  },
  {
    id: 'mercedes-eqe',
    brand: 'Mercedes-Benz',
    model: 'EQE',
    aliases: ['eqe'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 14000000,
    demand: 0.85,
    scrapFloor: 450000,
    fuels: ['Electric'],
    discontinued: true,
  },
  {
    id: 'bmw-2-series',
    brand: 'BMW',
    model: '2 Series Gran Coupe',
    aliases: ['2 series', '1 series', '220i'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4580000,
    demand: 0.88,
    scrapFloor: 190000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'bmw-x4',
    brand: 'BMW',
    model: 'X4',
    aliases: ['x4'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 7300000,
    demand: 0.88,
    scrapFloor: 290000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'bmw-x6',
    brand: 'BMW',
    model: 'X6',
    aliases: ['x6'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 17800000,
    demand: 0.88,
    scrapFloor: 400000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'bmw-i7',
    brand: 'BMW',
    model: 'i7',
    aliases: ['i7'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 21000000,
    demand: 0.85,
    scrapFloor: 550000,
    fuels: ['Electric'],
  },
  {
    id: 'bmw-ix',
    brand: 'BMW',
    model: 'iX',
    aliases: ['ix'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 14000000,
    demand: 0.85,
    scrapFloor: 450000,
    fuels: ['Electric'],
  },
  {
    id: 'audi-a3',
    brand: 'Audi',
    model: 'A3',
    aliases: ['a3'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4500000,
    demand: 0.88,
    scrapFloor: 180000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'audi-q2',
    brand: 'Audi',
    model: 'Q2',
    aliases: ['q2'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5000000,
    demand: 0.88,
    scrapFloor: 200000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'audi-etron',
    brand: 'Audi',
    model: 'e-tron / Q8 e-tron',
    aliases: ['e tron', 'etron'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 11500000,
    demand: 0.85,
    scrapFloor: 400000,
    fuels: ['Electric'],
    discontinued: true,
  },
  {
    id: 'volvo-xc40',
    brand: 'Volvo',
    model: 'XC40',
    aliases: ['xc40', 'xc 40'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4900000,
    demand: 0.88,
    scrapFloor: 230000,
    fuels: ['Petrol', 'Electric'],
    discontinued: true,
  },
  {
    id: 'volvo-c40',
    brand: 'Volvo',
    model: 'C40 Recharge',
    aliases: ['c40'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5900000,
    demand: 0.85,
    scrapFloor: 250000,
    fuels: ['Petrol', 'Electric'],
  },
  {
    id: 'volvo-s60',
    brand: 'Volvo',
    model: 'S60',
    aliases: ['s60', 's 60'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4800000,
    demand: 0.86,
    scrapFloor: 200000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'volvo-s80',
    brand: 'Volvo',
    model: 'S80',
    aliases: ['s80'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5500000,
    demand: 0.84,
    scrapFloor: 220000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'jaguar-xe',
    brand: 'Jaguar',
    model: 'XE',
    aliases: ['xe'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 4800000,
    demand: 0.86,
    scrapFloor: 190000,
    fuels: ['Petrol', 'Diesel'],
    discontinued: true,
  },
  {
    id: 'jaguar-f-pace',
    brand: 'Jaguar',
    model: 'F-Pace',
    aliases: ['f pace', 'fpace'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 7500000,
    demand: 0.88,
    scrapFloor: 290000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'landrover-evoque',
    brand: 'Land Rover',
    model: 'Range Rover Evoque',
    aliases: ['range rover evoque', 'evoque'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6486000,
    demand: 0.88,
    scrapFloor: 290000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'landrover-discovery-sport',
    brand: 'Land Rover',
    model: 'Discovery Sport',
    aliases: ['discovery sport', 'freelander'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6337000,
    demand: 0.88,
    scrapFloor: 285000,
    fuels: ['Petrol', 'Diesel'],
  },
  {
    id: 'lexus-nx',
    brand: 'Lexus',
    model: 'NX 350h',
    aliases: ['lexus nx', 'nx 350'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6759000,
    demand: 0.9,
    scrapFloor: 290000,
    fuels: ['Hybrid', 'Petrol'],
  },
  {
    id: 'lexus-rx',
    brand: 'Lexus',
    model: 'RX 350h',
    aliases: ['lexus rx', 'rx 350'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 8999000,
    demand: 0.9,
    scrapFloor: 350000,
    fuels: ['Hybrid', 'Petrol'],
  },
  {
    id: 'lexus-lx',
    brand: 'Lexus',
    model: 'LX 500d',
    aliases: ['lexus lx', 'lx 500'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 28100000,
    demand: 0.9,
    scrapFloor: 800000,
    fuels: ['Hybrid', 'Petrol'],
  },
  {
    id: 'lexus-lm',
    brand: 'Lexus',
    model: 'LM 350h',
    aliases: ['lexus lm', 'lm 350'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 22000000,
    demand: 0.95,
    scrapFloor: 600000,
    fuels: ['Hybrid'],
  },
  {
    id: 'porsche-macan',
    brand: 'Porsche',
    model: 'Macan',
    aliases: ['macan'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 8965000,
    demand: 0.87,
    scrapFloor: 350000,
    fuels: ['Petrol', 'Electric'],
  },
  {
    id: 'mini-cooper',
    brand: 'Mini',
    model: 'Cooper',
    aliases: ['mini cooper'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 5900000,
    demand: 0.85,
    scrapFloor: 180000,
    fuels: ['Petrol'],
  },
  {
    id: 'mini-countryman',
    brand: 'Mini',
    model: 'Countryman',
    aliases: ['countryman'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 6500000,
    demand: 0.85,
    scrapFloor: 250000,
    fuels: ['Petrol'],
  },
  {
    id: 'rolls-royce-phantom',
    brand: 'Rolls-Royce',
    model: 'Phantom',
    aliases: ['phantom'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 105000000,
    demand: 0.95,
    scrapFloor: 1800000,
    fuels: ['Petrol'],
  },
  {
    id: 'rolls-royce-ghost',
    brand: 'Rolls-Royce',
    model: 'Ghost',
    aliases: ['ghost'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 85000000,
    demand: 0.95,
    scrapFloor: 1500000,
    fuels: ['Petrol'],
  },
  {
    id: 'rolls-royce-cullinan',
    brand: 'Rolls-Royce',
    model: 'Cullinan',
    aliases: ['cullinan'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 100000000,
    demand: 0.95,
    scrapFloor: 1700000,
    fuels: ['Petrol'],
  },
  {
    id: 'bentley-continental',
    brand: 'Bentley',
    model: 'Continental GT',
    aliases: ['continental gt'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 85000000,
    demand: 0.9,
    scrapFloor: 1500000,
    fuels: ['Petrol'],
  },
  {
    id: 'bentley-flying-spur',
    brand: 'Bentley',
    model: 'Flying Spur',
    aliases: ['flying spur'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 80000000,
    demand: 0.92,
    scrapFloor: 1400000,
    fuels: ['Petrol'],
  },
  {
    id: 'bentley-bentayga',
    brand: 'Bentley',
    model: 'Bentayga',
    aliases: ['bentayga'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 60000000,
    demand: 0.9,
    scrapFloor: 1200000,
    fuels: ['Petrol'],
  },
  {
    id: 'maserati-ghibli',
    brand: 'Maserati',
    model: 'Ghibli',
    aliases: ['ghibli'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 14000000,
    demand: 0.84,
    scrapFloor: 500000,
    fuels: ['Petrol'],
  },
  {
    id: 'maserati-quattroporte',
    brand: 'Maserati',
    model: 'Quattroporte',
    aliases: ['quattroporte'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 22000000,
    demand: 0.86,
    scrapFloor: 650000,
    fuels: ['Petrol'],
  },
  {
    id: 'maserati-levante',
    brand: 'Maserati',
    model: 'Levante',
    aliases: ['levante'],
    category: 'Cars',
    group: LUX,
    curve: 'luxury',
    newPrice: 16000000,
    demand: 0.84,
    scrapFloor: 550000,
    fuels: ['Petrol'],
  },

  // ── Mini vans & tempo travellers — school, staff and tourist duty ─────────
  {
    id: 'maruti-eeco',
    brand: 'Maruti Suzuki',
    model: 'Eeco / Eeco Tour V',
    aliases: ['eeco tour', 'eeco'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 520353,
    demand: 1.02,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol'],
    newTaxiSlug: 'maruti-eeco-tour-v',
  },
  {
    id: 'maruti-omni',
    brand: 'Maruti Suzuki',
    model: 'Omni',
    aliases: ['omni'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 450000,
    demand: 0.9,
    scrapFloor: 35000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'tata-winger',
    brand: 'Tata',
    model: 'Winger',
    aliases: ['winger'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'muv',
    newPrice: 1600000,
    demand: 0.97,
    scrapFloor: 130000,
    fuels: ['Diesel'],
  },
  {
    id: 'force-traveller',
    brand: 'Force',
    model: 'Traveller / Tempo Traveller',
    aliases: ['tempo traveller', 'traveller'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'muv',
    newPrice: 1900000,
    demand: 1.03,
    scrapFloor: 150000,
    fuels: ['Diesel'],
  },
  {
    id: 'force-urbania',
    brand: 'Force',
    model: 'Urbania',
    aliases: ['urbania'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'muv',
    newPrice: 3200000,
    demand: 1.02,
    scrapFloor: 250000,
    fuels: ['Diesel'],
  },
  {
    id: 'force-trax',
    brand: 'Force',
    model: 'Trax / Toofan / Cruiser',
    aliases: ['trax toofan', 'toofan', 'trax', 'cruiser'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 1150000,
    demand: 0.92,
    scrapFloor: 90000,
    fuels: ['Diesel'],
  },
  {
    id: 'toyota-hiace',
    brand: 'Toyota',
    model: 'HiAce (imported)',
    aliases: ['hiace', 'hi ace'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'muv',
    newPrice: 4500000,
    demand: 0.92,
    scrapFloor: 250000,
    fuels: ['Diesel', 'Petrol'],
  },
  {
    id: 'mahindra-supro',
    brand: 'Mahindra',
    model: 'Supro / Supro Profit Van',
    aliases: ['supro'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 750000,
    demand: 0.93,
    scrapFloor: 55000,
    fuels: ['Diesel', 'CNG', 'Electric'],
  },
  {
    id: 'tata-magic',
    brand: 'Tata',
    model: 'Magic / Magic Express',
    aliases: ['magic express', 'magic'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 650000,
    demand: 0.95,
    scrapFloor: 50000,
    fuels: ['Diesel', 'CNG'],
  },

  {
    id: 'maruti-versa',
    brand: 'Maruti Suzuki',
    model: 'Versa',
    aliases: ['versa'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 500000,
    demand: 0.84,
    scrapFloor: 32000,
    fuels: ['Petrol', 'CNG'],
    discontinued: true,
  },
  {
    id: 'maruti-super-carry',
    brand: 'Maruti Suzuki',
    model: 'Super Carry',
    aliases: ['super carry'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 550000,
    demand: 0.88,
    scrapFloor: 40000,
    fuels: ['CNG', 'Diesel'],
  },
  {
    id: 'nissan-evalia',
    brand: 'Nissan',
    model: 'Evalia',
    aliases: ['evalia'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 900000,
    demand: 0.82,
    scrapFloor: 55000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'ashok-leyland-stile',
    brand: 'Ashok Leyland',
    model: 'Stile',
    aliases: ['stile'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 900000,
    demand: 0.82,
    scrapFloor: 55000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'mahindra-maxximo',
    brand: 'Mahindra',
    model: 'Maxximo Mini Van',
    aliases: ['maxximo'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 600000,
    demand: 0.86,
    scrapFloor: 40000,
    fuels: ['Diesel', 'CNG'],
    discontinued: true,
  },
  {
    id: 'mahindra-voyager',
    brand: 'Mahindra',
    model: 'Voyager',
    aliases: ['voyager'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 550000,
    demand: 0.82,
    scrapFloor: 35000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'tata-venture',
    brand: 'Tata',
    model: 'Venture',
    aliases: ['venture'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 600000,
    demand: 0.84,
    scrapFloor: 40000,
    fuels: ['Diesel'],
    discontinued: true,
  },
  {
    id: 'force-matador',
    brand: 'Force',
    model: 'Matador',
    aliases: ['matador'],
    category: 'Mini Vans',
    group: VAN,
    curve: 'van',
    newPrice: 800000,
    demand: 0.85,
    scrapFloor: 60000,
    fuels: ['Diesel'],
    discontinued: true,
  },

  // ── Buses & staff carriers ───────────────────────────────────────────────
  {
    id: 'force-monobus',
    brand: 'Force',
    model: 'Monobus / Traveller 26-seater',
    aliases: ['monobus'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 2600000,
    demand: 0.98,
    scrapFloor: 220000,
    fuels: ['Diesel'],
  },
  {
    id: 'tata-starbus',
    brand: 'Tata',
    model: 'Starbus (32–40 seater)',
    aliases: ['starbus', 'star bus'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 2800000,
    demand: 0.96,
    scrapFloor: 250000,
    fuels: ['Diesel', 'CNG'],
  },
  {
    id: 'tata-lp-712',
    brand: 'Tata',
    model: 'LP 712 / LP 909 (school & staff bus)',
    aliases: ['lp 712', 'lp712', 'lp 909', 'lp909'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 2200000,
    demand: 0.95,
    scrapFloor: 200000,
    fuels: ['Diesel', 'CNG'],
  },
  {
    id: 'tata-lpo-1515',
    brand: 'Tata',
    model: 'LPO 1512 / LPO 1515 (intercity)',
    aliases: ['lpo 1515', 'lpo1515', 'lpo 1512', 'lpo1512', 'lpo'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 3800000,
    demand: 0.93,
    scrapFloor: 300000,
    fuels: ['Diesel', 'CNG'],
  },
  {
    id: 'eicher-skyline',
    brand: 'Eicher',
    model: 'Skyline / Skyline Pro / Starline',
    aliases: ['skyline', 'starline'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 3200000,
    demand: 0.95,
    scrapFloor: 280000,
    fuels: ['Diesel'],
  },
  {
    id: 'ashok-leyland-bus',
    brand: 'Ashok Leyland',
    model: 'Sunshine / Oyster / Falcon / Viking',
    aliases: ['sunshine', 'oyster', 'falcon', 'viking', 'cheetah', 'lynx'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 3000000,
    demand: 0.94,
    scrapFloor: 260000,
    fuels: ['Diesel', 'CNG'],
  },
  {
    id: 'sml-isuzu-bus',
    brand: 'SML Isuzu',
    model: 'S7 / Executive (Swaraj Mazda)',
    aliases: ['sml', 'swaraj mazda'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 2500000,
    demand: 0.92,
    scrapFloor: 200000,
    fuels: ['Diesel', 'CNG'],
  },
  {
    id: 'mahindra-cruzio',
    brand: 'Mahindra',
    model: 'Cruzio / Cruzio Grande (school bus)',
    aliases: ['cruzio'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 1950000,
    demand: 0.94,
    scrapFloor: 170000,
    fuels: ['Diesel'],
  },

  {
    id: 'tata-407',
    brand: 'Tata',
    model: 'LP 407 / 709 (mini bus)',
    aliases: ['lp 407', 'lp407', '407', '709'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 1800000,
    demand: 0.92,
    scrapFloor: 160000,
    fuels: ['Diesel'],
  },
  {
    id: 'volvo-bus',
    brand: 'Volvo',
    model: '9400 / B7R / B9R (coach)',
    aliases: ['volvo bus', '9400', 'b7r', 'b9r'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 12000000,
    demand: 0.9,
    scrapFloor: 900000,
    fuels: ['Diesel'],
  },
  {
    id: 'bharatbenz-bus',
    brand: 'BharatBenz',
    model: '917 / 1017 (school & staff bus)',
    aliases: ['bharatbenz', 'bharat benz', '917', '1017'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 4500000,
    demand: 0.9,
    scrapFloor: 350000,
    fuels: ['Diesel'],
  },
  {
    id: 'scania-bus',
    brand: 'Scania',
    model: 'Metrolink / Touring (coach)',
    aliases: ['metrolink', 'scania'],
    category: 'Buses',
    group: BUS,
    curve: 'bus',
    newPrice: 11000000,
    demand: 0.85,
    scrapFloor: 800000,
    fuels: ['Diesel'],
    discontinued: true,
  },

  // ── Escape hatch — anything not listed above ─────────────────────────────
  // The catalogue will never be complete, and a seller bouncing off "my model
  // isn't here" is a lead we lose. Picking this reveals a manual price field.
  {
    id: 'other',
    brand: 'Other',
    model: 'My model is not listed',
    category: 'Cars',
    curve: 'car',
    newPrice: null,
    demand: 0.95,
    scrapFloor: 45000,
    fuels: ['CNG', 'Petrol', 'Diesel', 'Electric', 'Hybrid'],
    manualPrice: true,
  },
];

export const getModelById = (id) => valuationModels.find((m) => m.id === id) || null;

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Extra brand spellings a seller might actually type. The catalogue's own brand
 * name is always matched too — this only covers what it does not say.
 */
const BRAND_ALIASES = {
  'Maruti Suzuki': ['maruti', 'suzuki'],
  'Ashok Leyland': ['ashok', 'leyland'],
  'Mercedes-Benz': ['mercedes', 'merc', 'benz'],
  'Land Rover': ['range rover', 'landrover'],
  'SML Isuzu': ['sml', 'swaraj mazda'],
  'Hindustan Motors': ['hindustan', 'hm'],
  'Rolls-Royce': ['rolls royce', 'rolls'],
  BharatBenz: ['bharat benz', 'daimler'],
  Chevrolet: ['chevy'],
  Force: ['force motors'],
  Volkswagen: ['vw'],
  MG: ['morris garages'],
  Citroen: ['citroën'],
};

/**
 * Best-effort match from what a seller typed into the listing form to a
 * catalogue model. Brand and model are free text there, so this is deliberately
 * forgiving: it scores every alias that appears in the text and takes the
 * longest, with a large bonus when the brand agrees.
 *
 * Returns null rather than guessing when nothing matches — a wrong benchmark is
 * worse than no benchmark, because the seller will price against it.
 */
export function findModel({ brand = '', model = '', category = '' } = {}) {
  const haystack = norm(`${brand} ${model}`);
  if (!haystack) return null;
  const brandText = norm(brand);

  let best = null;
  for (const m of valuationModels) {
    if (m.manualPrice) continue;
    const brandNames = [m.brand, ...(BRAND_ALIASES[m.brand] || [])].map(norm);
    const brandOk = !brandText || brandNames.some((b) => brandText.includes(b) || b.includes(brandText));

    for (const alias of m.aliases || []) {
      const a = norm(alias);
      if (!haystack.includes(a)) continue;
      const score = a.length + (brandOk ? 100 : 0) + (category && m.category === category ? 10 : 0);
      if (!best || score > best.score) best = { model: m, score };
    }
  }
  return best ? best.model : null;
}

/**
 * Grouped for the <select>. The DB's three categories are too coarse to pick
 * from once the catalogue runs past a hundred models — nearly all of them are
 * 'Cars' — so the picker groups by bodystyle and duty instead.
 */
const GROUP_ORDER = [HATCH, SEDAN, MUV, EV, LUX, VAN, BUS];

/**
 * The models that actually turn up, in the order they turn up in.
 *
 * A picker this long buries a Dzire behind thirty cars nobody drives on a
 * permit, and a seller who has to scroll for their own vehicle is a seller who
 * gives up. These float to the top of their group; everything else keeps
 * catalogue order behind them.
 *
 * Ranked on what BST actually lists and what the trade asks for — not on
 * national sales figures, which are a private-buyer measure.
 */
const POPULAR_FIRST = [
  'maruti-dzire', 'toyota-innova-crysta', 'maruti-ertiga', 'maruti-wagonr',
  'maruti-eeco', 'hyundai-aura', 'toyota-innova-hycross', 'force-traveller',
  'maruti-swift', 'toyota-etios', 'honda-amaze', 'tata-tigor',
  'toyota-innova-old', 'hyundai-xcent', 'mahindra-bolero', 'toyota-rumion',
  'kia-carens', 'maruti-celerio', 'tata-winger', 'toyota-fortuner',
  'maruti-spresso', 'hyundai-grand-i10', 'maruti-ciaz', 'mercedes-e-class',
  'toyota-camry', 'audi-a6', 'bmw-5-series', 'force-urbania',
];

const rank = (id) => {
  const i = POPULAR_FIRST.indexOf(id);
  return i === -1 ? POPULAR_FIRST.length : i;
};

export const modelGroups = GROUP_ORDER.map((label) => ({
  label,
  models: valuationModels
    .filter((m) => m.group === label)
    // Stable sort: only the popular ones move, the rest stay as authored.
    .map((m, i) => ({ m, i }))
    .sort((a, b) => rank(a.m.id) - rank(b.m.id) || a.i - b.i)
    .map(({ m }) => m),
})).filter((g) => g.models.length > 0);

/**
 * Same groups, narrowed by a search box. At 250-odd models the picker stopped
 * being scrollable — a seller looking for a Qualis should not have to find it
 * among thirty other MUVs. Matches on aliases too, so typing the trim name or a
 * misspelling still lands ("dezire", "crysta", "xpres").
 */
export function filterModelGroups(query) {
  const q = norm(query);
  if (!q) return modelGroups;
  const hit = (m) => norm(`${m.brand} ${m.model} ${(m.aliases || []).join(' ')}`).includes(q);
  return modelGroups
    .map((g) => ({ ...g, models: g.models.filter(hit) }))
    .filter((g) => g.models.length > 0);
}
